import { API_BASE } from '@/utils/env'

/**
 * 认证接口（登录 / 续期）—— 不走 `request.ts`，因为这两个调用本身不需要
 * Authorization 头（登录用账号密码，续期用 refreshToken）。
 */
interface ApiEnvelope<T> {
  code: number
  msg: string
  data: T
}

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
  } catch (err) {
    throw new Error(`无法连接认证接口：${(err as Error).message}`)
  }

  let payload: ApiEnvelope<T> | null = null
  try {
    payload = (await res.json()) as ApiEnvelope<T>
  } catch (err) {
    throw new Error(`认证接口返回非 JSON（HTTP ${res.status}）`)
  }
  if (!res.ok || !payload || payload.code !== 0) {
    throw new AuthError(payload?.msg || `HTTP ${res.status}`, res.status)
  }
  return payload.data
}

/** 认证失败（含 HTTP 状态）：401/403 表示凭据被拒，区别于网络/服务故障。 */
export class AuthError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'AuthError'
    this.status = status
  }
}

/** `POST /admin-api/system/auth/login` 的响应（凭证字段可能缺省，按需兜底）。 */
export interface LoginResult {
  accessToken: string
  refreshToken?: string
  expiresTime?: number
  tenantId?: number
  mustChangePassword?: boolean
}

export function login(username: string, password: string, tenantId = 1): Promise<LoginResult> {
  return postJson<LoginResult>('/system/auth/login', { username, password, tenantId })
}

/**
 * `POST /admin-api/system/auth/refresh-token?refreshToken=xxx`（公开路由）。
 * 响应只有新的 accessToken —— 原 refreshToken 在 7 天内继续有效。
 */
export function refreshAccessToken(refreshToken: string): Promise<{ accessToken: string }> {
  return postJson<{ accessToken: string }>(
    `/system/auth/refresh-token?refreshToken=${encodeURIComponent(refreshToken)}`
  )
}
